// apps/frontend/src/components/RealtimeNotificationToasts.tsx
// "Drop alerts" temps réel : écoute le WebSocket et affiche un toast
// Glowp premium pour les nouvelles notifs "engageantes" (drop boutique
// suivie, like reçu, suivi, offre, vente). Click → navigate vers le
// produit / la conversation / la commande.
//
// Évite : MESSAGE (déjà géré par le bell de la nav, sinon ça spam).
//
// Stack vertical en bas-droite, 4 max simultanément, auto-dismiss 6s.

import React, { useEffect, useRef, useState } from 'react';
import {
  Heart,
  UserPlus,
  Store,
  Tag,
  ShoppingBag,
  Sparkles,
  AlertCircle,
  X,
  ArrowRight,
} from 'lucide-react';
import wsService, {
  type NotificationPayload,
} from '../services/websocketService';
import { useAuth } from '../hooks/useAuth';
import { haptic } from '../lib/haptic';
import { getCloudinaryBlurUrl } from '../utils/cloudinaryBlur';

import { glowpNavigate } from '../lib/navigate';
const AUTO_DISMISS_MS = 6000;
const MAX_STACK = 4;

// Types qu'on toast vs types qu'on ignore (déjà gérés ailleurs ou trop bruyants)
const TOASTED_TYPES = new Set([
  'PRODUCT_LIKED',
  'SHOP_FOLLOWED',
  'USER_FOLLOWED',
  'OFFER_RECEIVED',
  'OFFER_ACCEPTED',
  'OFFER_REJECTED',
  'PRODUCT_SOLD',
  'PRODUCT_RESERVED',
  'ORDER',
  'PAYMENT',
  'SHOP_POST',
  'EVENT_REMINDER',
  // Notifs admin/système actionnables (nudges, alertes) : on les toast aussi
  // pour ramener immédiatement le vendeur sur l'action attendue.
  'SYSTEM',
]);

interface ActiveToast {
  notif: NotificationPayload;
  bornAt: number;
}

function iconFor(type: string): React.ReactNode {
  const cls = 'w-4 h-4 text-white';
  if (type === 'PRODUCT_LIKED') return <Heart className={cls} fill="white" />;
  if (type === 'USER_FOLLOWED' || type === 'SHOP_FOLLOWED')
    return <UserPlus className={cls} />;
  if (type === 'SHOP_POST') return <Store className={cls} />;
  if (type.startsWith('OFFER_')) return <Tag className={cls} />;
  if (type === 'ORDER' || type === 'PAYMENT' || type === 'PRODUCT_SOLD')
    return <ShoppingBag className={cls} />;
  if (type === 'SYSTEM') return <AlertCircle className={cls} />;
  return <Sparkles className={cls} />;
}

const RealtimeNotificationToasts: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    if (!isAuthenticated) return;

    const off = wsService.onNewNotification(notif => {
      if (!notif || !notif.id) return;
      if (seenIds.current.has(notif.id)) return;
      if (!TOASTED_TYPES.has(notif.type)) return;
      seenIds.current.add(notif.id);

      // Petit haptic discret pour signaler l'arrivée (uniquement si onglet visible)
      if (typeof document !== 'undefined' && !document.hidden) {
        haptic.tap();
      }

      setToasts(prev => {
        const next = [
          { notif, bornAt: Date.now() },
          ...prev.slice(0, MAX_STACK - 1),
        ];
        return next;
      });
    });

    return () => {
      off?.();
    };
  }, [isAuthenticated]);

  // Auto-dismiss : tick toutes les 500ms pour expirer les vieux toasts
  useEffect(() => {
    if (toasts.length === 0) return;
    const t = window.setInterval(() => {
      const now = Date.now();
      setToasts(prev =>
        prev.filter(item => now - item.bornAt < AUTO_DISMISS_MS)
      );
    }, 500);
    return () => clearInterval(t);
  }, [toasts.length]);

  const dismiss = (id: string) => {
    setToasts(prev => prev.filter(t => t.notif.id !== id));
  };

  const handleAction = (notif: NotificationPayload) => {
    dismiss(notif.id);
    const url = notif.actionUrl;
    if (!url) return;
    // HashRouter : on assigne le hash pour rester compatible
    if (url.startsWith('/')) {
      glowpNavigate(`#${url}`);
    } else {
      window.location.href = url;
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed right-3 sm:right-4 z-[9500] flex flex-col gap-2 pointer-events-none"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
        maxWidth: 'min(360px, calc(100vw - 1.5rem))',
      }}
      role="status"
      aria-live="polite"
    >
      {toasts.map(({ notif }) => {
        const blur = getCloudinaryBlurUrl(notif.imageUrl);
        return (
          <div
            key={notif.id}
            className="pointer-events-auto bg-white rounded-2xl shadow-xl ring-1 ring-black/5 overflow-hidden animate-[dropToastIn_0.4s_cubic-bezier(0.25,0.85,0.4,1.15)]"
          >
            <div className="flex items-stretch">
              {/* Vignette image si dispo (drop d'une boutique avec preview) */}
              {notif.imageUrl && (
                <div className="relative w-14 flex-shrink-0 bg-brand-light overflow-hidden">
                  {blur && (
                    <img
                      src={blur}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: 'blur(8px)', transform: 'scale(1.06)' }}
                    />
                  )}
                  <img
                    src={notif.imageUrl}
                    alt=""
                    className="relative w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Contenu */}
              <div className="flex-1 min-w-0 p-3 flex items-start gap-2.5">
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background:
                      'linear-gradient(135deg, #C8A882 0%, #B89968 100%)',
                  }}
                >
                  {iconFor(notif.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-brand-dark truncate">
                    {notif.title}
                  </div>
                  <div className="text-xs text-brand-warm mt-0.5 line-clamp-2">
                    {notif.message}
                  </div>
                  {notif.actionUrl && (
                    <button
                      onClick={() => handleAction(notif)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:text-brand-dark transition-colors"
                    >
                      {notif.actionLabel || 'Voir'}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <button
                  onClick={() => dismiss(notif.id)}
                  className="flex-shrink-0 p-1 text-brand-warm hover:text-brand-dark transition-colors -mr-1 -mt-1"
                  aria-label="Fermer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes dropToastIn {
          from { opacity: 0; transform: translateX(40px) scale(0.95); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default RealtimeNotificationToasts;
